/**
 * Session state — Plex-classic (ARCHITECTURE §5.1 #1): ONE opaque session
 * token per device that the browser keeps and the API only ever checks
 * (`Authorization: Bearer`). Nothing refreshes, rotates or expires, so
 * nothing here can race: a second tab, a reload storm, a laptop waking with
 * ten 401s at once, a clock two hours off — none of it can sign anyone out.
 * The only verdict is the broker's own: a 401 from GET /v1/me (or a sign-in
 * call) means the session is gone; anything else — offline, 5xx, 429 — keeps
 * the session as `offline` and retries in the background.
 *
 * Two stores, chosen at build time:
 * - hosted (app.rago.tv): the Plex-shaped rago_tv_auth cookie the API sets on
 *   rago.tv — profile + token, readable by script (lib/authCookie). Fetches
 *   to the API go credentialed so GET /v1/me can re-set that cookie; the
 *   credential itself is always the Bearer header.
 * - local mode (the server-hosted app on a plain-http LAN origin, where no
 *   rago.tv cookie can exist): localStorage rago.local.auth
 *   (lib/localAuthStore), the way Plex's `/web` keeps its token.
 */
import type { MeResponse, ProblemDetails, SignInResponse, User } from "@rago/web-shared";
import { API_BASE } from "./api";
import { isLocalMode } from "./appMode";
import { clearAuthCookieLocally, ensureAuthCookie, readAuthCookie } from "./authCookie";
import { deviceInfoPayload, getDeviceIdentity } from "./deviceKeys";
import { LOCAL_AUTH_KEY, clearLocalState, readLocalAuth, writeLocalAuth } from "./localAuthStore";

export type AuthState =
  | { status: "loading" }
  | { status: "signedOut" }
  | {
      status: "signedIn";
      user: User;
      /** The broker could not be reached to confirm the session; it is
       * kept (the stored token is still the credential) and re-checked in
       * the background. In local mode the media server keeps working
       * through its own tokens (lib/localSession). */
      offline?: boolean;
    };

type Listener = (state: AuthState) => void;

export class ApiProblemError extends Error {
  constructor(public problem: ProblemDetails) {
    super(problem.detail ?? problem.title);
  }
}

/** The broker did not answer (network error, timeout, 5xx) — nothing about
 * the session is known to be wrong. */
export class CloudUnreachableError extends Error {
  constructor(cause?: unknown) {
    super("Rago's servers can't be reached");
    this.name = "CloudUnreachableError";
    this.cause = cause;
  }
}

/** A problem response's human line (detail, else title); undefined for
 * anything that is not one — the settings pages' per-control Apply errors. */
export function problemMessage(err: unknown): string | undefined {
  if (err instanceof ApiProblemError) return err.problem.detail ?? err.problem.title;
  return undefined;
}

/** Pure: what a failed GET /v1/me means. Only a 401 is a verdict on the
 * session; a thrown fetch, a 5xx, a 429 or any other status is the cloud
 * being away and keeps the session. */
export function classifyMeFailure(
  outcome: { thrown: unknown } | { status: number },
): "signedOut" | "offline" {
  if ("thrown" in outcome) return "offline";
  return outcome.status === 401 ? "signedOut" : "offline";
}

const ME_TIMEOUT_MS = 10_000;
const SIGNOUT_TIMEOUT_MS = 5_000;
/** Offline re-check cadence: a minute, then doubling up to five. */
const OFFLINE_RETRY_MIN_MS = 60_000;
const OFFLINE_RETRY_MAX_MS = 5 * 60_000;

interface StoredSession {
  sessionToken: string;
  user: User;
}

/** The browser's copy of the session: the cookie (hosted) or localStorage
 * (local mode), read at call time so every tab sees the same thing. */
function readStore(): StoredSession | null {
  if (isLocalMode) {
    const s = readLocalAuth();
    return s ? { sessionToken: s.sessionToken, user: s.user } : null;
  }
  const c = readAuthCookie();
  return c ? { sessionToken: c.sessionToken, user: c.user } : null;
}

class AuthManager {
  private state: AuthState = { status: "loading" };
  private listeners = new Set<Listener>();
  private channel: BroadcastChannel | null = null;
  private bootstrapped = false;
  private verifying: Promise<void> | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private offlineRetryMs = OFFLINE_RETRY_MIN_MS;
  private wakeArmed = false;

  getState(): AuthState {
    return this.state;
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private setState(next: AuthState): void {
    this.state = next;
    for (const fn of this.listeners) fn(next);
  }

  /** Other tabs: a sign-in or sign-out anywhere lands everywhere. The
   * store itself is shared already (one cookie jar, one localStorage); the
   * channel just saves the other tabs a round trip. Local mode also hears
   * the `storage` event, which fires without any channel. */
  private ensureChannel(): void {
    if (this.channel || typeof BroadcastChannel === "undefined") return;
    this.channel = new BroadcastChannel("rago-auth");
    this.channel.onmessage = (ev: MessageEvent) => {
      const msg = ev.data as { type: "signedIn"; user: User } | { type: "signedOut" };
      if (msg.type === "signedIn") this.setState({ status: "signedIn", user: msg.user });
      else if (msg.type === "signedOut") this.clearLocal(false);
    };
    if (isLocalMode && typeof window !== "undefined") {
      window.addEventListener("storage", (ev) => {
        if (ev.key !== LOCAL_AUTH_KEY && ev.key !== null) return;
        const stored = readLocalAuth();
        if (!stored) this.clearLocal(false);
        else this.setState({ status: "signedIn", user: stored.user });
      });
    }
  }

  /** While offline, coming back online or to the foreground re-checks at once. */
  private armWake(): void {
    if (this.wakeArmed || typeof window === "undefined") return;
    this.wakeArmed = true;
    const wake = () => {
      if (this.isOffline()) void this.verifyMe();
    };
    window.addEventListener("online", wake);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") wake();
    });
  }

  private clearRetry(): void {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
  }

  private scheduleRetry(): void {
    this.clearRetry();
    const delay = this.offlineRetryMs;
    this.offlineRetryMs = Math.min(this.offlineRetryMs * 2, OFFLINE_RETRY_MAX_MS);
    this.retryTimer = setTimeout(() => void this.verifyMe(), delay);
  }

  private adoptUser(user: User, broadcast: boolean): void {
    this.ensureChannel();
    this.offlineRetryMs = OFFLINE_RETRY_MIN_MS;
    this.clearRetry();
    this.setState({ status: "signedIn", user });
    if (broadcast) this.channel?.postMessage({ type: "signedIn", user });
  }

  private markOffline(user: User): void {
    this.setState({ status: "signedIn", user, offline: true });
    this.armWake();
    this.scheduleRetry();
  }

  private clearLocal(broadcast: boolean): void {
    this.clearRetry();
    this.setState({ status: "signedOut" });
    if (broadcast) this.channel?.postMessage({ type: "signedOut" });
  }

  /** The broker said the session is gone: forget it everywhere. */
  private rejectSession(): void {
    if (isLocalMode) clearLocalState();
    else clearAuthCookieLocally();
    this.clearLocal(true);
  }

  /** Reload path: paint from the stored profile at once (Plex reads
   * plex_tv_auth the same way) and let the broker's verdict arrive in the
   * background — a stale session shows for the one round trip it takes
   * GET /v1/me to say so, never a spinner. */
  async bootstrap(): Promise<void> {
    if (this.bootstrapped) return;
    this.bootstrapped = true;
    this.ensureChannel();
    const stored = readStore();
    if (!stored) {
      this.clearLocal(false);
      return;
    }
    this.setState({ status: "signedIn", user: stored.user });
    void this.verifyMe();
  }

  /** GET /v1/me — the only place a session is judged. Single-flight: ten
   * callers at once share one request. */
  verifyMe(): Promise<void> {
    this.verifying ??= this.doVerify().finally(() => {
      this.verifying = null;
    });
    return this.verifying;
  }

  private async doVerify(): Promise<void> {
    const stored = readStore();
    if (!stored) {
      if (this.state.status === "signedIn") this.clearLocal(false);
      return;
    }
    let res: Response;
    try {
      res = await fetch(`${API_BASE}/v1/me`, {
        credentials: isLocalMode ? "omit" : "include",
        headers: { authorization: `Bearer ${stored.sessionToken}` },
        cache: "no-store",
        signal: AbortSignal.timeout(ME_TIMEOUT_MS),
      });
    } catch {
      this.markOffline(stored.user);
      return;
    }
    if (!res.ok) {
      if (classifyMeFailure({ status: res.status }) === "signedOut") this.rejectSession();
      else this.markOffline(stored.user);
      return;
    }
    const body = (await res.json().catch(() => null)) as MeResponse | null;
    if (!body?.user) {
      this.markOffline(stored.user);
      return;
    }
    // Hosted: the response re-set the cookie with this profile. Local: keep
    // the stored copy as fresh.
    if (isLocalMode) writeLocalAuth({ v: 2, sessionToken: stored.sessionToken, user: body.user });
    this.adoptUser(body.user, false);
  }

  /** apiClient: the broker answered 401 to `usedToken`. Another tab may have
   * signed in since (the store moved on) — adopt that; otherwise let
   * GET /v1/me judge. Never a sign-out on the word of some other route. */
  async onUnauthorized(usedToken: string): Promise<void> {
    const stored = readStore();
    if (stored && stored.sessionToken !== usedToken) {
      this.adoptUser(stored.user, false);
      return;
    }
    await this.verifyMe();
  }

  /** Re-fetch the profile (after an email verification, a PATCH elsewhere). */
  reloadMe(): Promise<void> {
    return this.verifyMe();
  }

  private adoptSession(session: SignInResponse): void {
    if (isLocalMode) {
      writeLocalAuth({ v: 2, sessionToken: session.sessionToken, user: session.user });
    } else {
      ensureAuthCookie(session.user, session.sessionToken);
    }
    this.adoptUser(session.user, true);
  }

  private async credentialedAuthCall(
    path: string,
    payload: Record<string, unknown>,
  ): Promise<SignInResponse> {
    const identity = await getDeviceIdentity();
    const res = await fetch(`${API_BASE}${path}`, {
      method: "POST",
      credentials: isLocalMode ? "omit" : "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        ...payload,
        device: await deviceInfoPayload(identity),
        transport: isLocalMode ? "body" : "cookie",
      }),
    });
    const json = (await res.json().catch(() => null)) as SignInResponse | ProblemDetails | null;
    if (!res.ok || !json || !("sessionToken" in json)) {
      throw new ApiProblemError(
        (json as ProblemDetails | null) ?? {
          type: "about:blank",
          title: "Request failed",
          status: res.status,
          code: "internal",
        },
      );
    }
    this.adoptSession(json);
    return json;
  }

  signIn(email: string, password: string, otp?: string): Promise<SignInResponse> {
    return this.credentialedAuthCall("/v1/auth/signin", { email, password, ...(otp ? { otp } : {}) });
  }

  signUp(email: string, username: string, password: string): Promise<SignInResponse> {
    return this.credentialedAuthCall("/v1/auth/signup", { email, username, password });
  }

  /** Local mode: a session redeemed from an approved link pin. */
  adoptPinSession(session: SignInResponse): void {
    this.adoptSession(session);
  }

  /** Always leaves locally, whatever the broker says: the row is revoked
   * when the call lands; offline, it lives on until Security › Sessions
   * or the next sign-in on this device replaces it. */
  async signOut(allDevices = false): Promise<void> {
    await this.postSignOut(this.sessionToken(), allDevices);
    if (isLocalMode) clearLocalState();
    else clearAuthCookieLocally();
    this.clearLocal(true);
  }

  /** The account behind the session is gone (Settings › Account › Delete
   * account): the broker ended every session with it, so there is nothing
   * to post — forget it here and tell the other tabs. */
  forgetSession(): void {
    this.rejectSession();
  }

  /** Local mode: revokes a session this page redeemed but never adopted
   * (the account has no access to this server, lib/localAccess) so it does
   * not linger under Security › Sessions. Nothing local changes. */
  discardSession(sessionToken: string): Promise<void> {
    return this.postSignOut(sessionToken, false);
  }

  private async postSignOut(token: string | null, allDevices: boolean): Promise<void> {
    try {
      await fetch(`${API_BASE}/v1/auth/signout`, {
        method: "POST",
        credentials: isLocalMode ? "omit" : "include",
        headers: {
          "content-type": "application/json",
          ...(token ? { authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ allDevices }),
        keepalive: true,
        signal: AbortSignal.timeout(SIGNOUT_TIMEOUT_MS),
      });
    } catch {
      // unreachable: the local state goes regardless
    }
  }

  /** The Bearer for broker calls — read from the store at call time, so a
   * newer sign-in in another tab is picked up without any message. */
  sessionToken(): string | null {
    return readStore()?.sessionToken ?? null;
  }

  isOffline(): boolean {
    return this.state.status === "signedIn" && this.state.offline === true;
  }

  /** Refresh user profile fields after a PATCH elsewhere. */
  updateUser(user: User): void {
    if (this.state.status !== "signedIn") return;
    this.setState({ ...this.state, user });
    if (isLocalMode) {
      const stored = readLocalAuth();
      if (stored) writeLocalAuth({ ...stored, user });
    }
  }
}

export const auth = new AuthManager();
